import { useState } from "react";
import { Check, Plus, Tag } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { labelStyle } from "@/lib/labelColors";
import { cn } from "@/lib/utils";

// Pick existing labels or type a new one and press Enter to create + attach it.
export default function LabelPicker({ labels, selected, onToggle, onCreate, trigger, testId = "label-picker" }) {
  const [q, setQ] = useState("");
  const query = q.trim();
  const shown = labels.filter((l) => l.name.toLowerCase().includes(query.toLowerCase()));
  const exists = labels.some((l) => l.name.toLowerCase() === query.toLowerCase());
  const create = async () => {
    if (!query || exists) return;
    await onCreate(query);
    setQ("");
  };
  return (
    <Popover onOpenChange={(o) => !o && setQ("")}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2" data-testid={`${testId}-popover`}>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); exists ? onToggle(labels.find((l) => l.name.toLowerCase() === query.toLowerCase()).name) : create(); } }}
          placeholder="Find or create a label…"
          maxLength={50}
          className="w-full h-8 px-2.5 rounded-lg border border-border bg-transparent text-sm outline-none focus:border-amber-400"
          data-testid={`${testId}-input`}
        />
        <div className="mt-1.5 max-h-52 overflow-y-auto">
          {shown.map((l) => {
            const on = selected.includes(l.name);
            return (
              <button key={l.id} type="button" onClick={() => onToggle(l.name)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-stone-100 dark:hover:bg-stone-800 text-left"
                data-testid={`${testId}-option-${l.name}`}>
                <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", labelStyle(labels, l.name).dot)} />
                <span className="flex-1 truncate">{l.name}</span>
                {on && <Check className="h-3.5 w-3.5 text-amber-600" />}
              </button>
            );
          })}
          {!shown.length && !query && <p className="px-2 py-1.5 text-xs text-stone-500">No labels yet — type a name to create one</p>}
          {query && !exists && (
            <button type="button" onClick={create} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40" data-testid={`${testId}-create`}>
              <Plus className="h-3.5 w-3.5" /> Create “{query}”
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const LabelPillTrigger = ({ count, ...props }) => (
  <button type="button" {...props} className="inline-flex items-center gap-1 rounded-full border border-dashed border-stone-300 dark:border-stone-600 px-2 py-0.5 text-xs text-stone-600 dark:text-stone-300 hover:border-amber-400 hover:text-amber-700 transition-colors">
    <Tag className="h-3 w-3" /> {count ? "Labels" : "Add label"}
  </button>
);
